import { Grid, Portal } from "@mui/material";
import AudienceAnalyticsProvider, {
  useAudienceAnalyticsContext,
} from "Providers/AudienceAnalyticsProvider";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import Loading from "Components/Loading/Loading";
import { useMemo } from "react";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  AnalyticsChartStatCard,
  AnalyticsFilterDropdown,
} from "features/analytics";
import { PageSubAction } from "consts";
import {
  CHART_KEY_AUDIENCE_DEEP_DIVE_AUDIENCE_PRIMARY_METRICS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_AVG_TIME_ON_PAGE,
  CHART_KEY_AUDIENCE_DEEP_DIVE_CHANNEL_INSIGHTS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_CHANNEL_PRIMARY_METRICS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_CHANNEL_ROI_LTV_METRICS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_EFFECTIVE_SESSIONS_AND_PAGEVIEWS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_INTERACTIONS_AND_CONVERSIONS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_LANDING_PAGE_PRIMARY_METRICS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_PRODUCT_CATEGORY_INSIGHTS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_PRODUCT_PRIMARY_METRICS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_SESSIONS_AND_PAGEVIEWS,
  CHART_KEY_AUDIENCE_DEEP_DIVE_TOP_CHANNELS_RADAR,
  CHART_KEY_AUDIENCE_DEEP_DIVE_TOP_PRODUCT_CATEGORY_RADAR,
  CHART_KEY_AUDIENCE_DEEP_DIVE_VISITS_PRIOR_CONVERSION_BY_CHANNEL,
} from "features/analytics/constants/chartKeys";

function AudienceDeepDivePageInner() {
  const { currentAudience, setCurrentAudience } = useAudienceAnalyticsContext();
  const attributes = useMemo(
    () => [
      {
        key: ApiDimension.DIMENSION_LEAD_AUDIENCE,
        value: currentAudience,
      },
    ],
    [currentAudience]
  );

  return (
    <>
      <Portal container={() => document.getElementById(PageSubAction)}>
        <AnalyticsFilterDropdown
          dimension={ApiDimension.DIMENSION_LEAD_AUDIENCE}
          maxWidth={250}
          disableBlank
          defaultIndex={0}
          onChange={(v) => {
            setCurrentAudience(v);
          }}
        />
      </Portal>
      {!currentAudience ? (
        <Loading />
      ) : (
        <Grid container spacing={2} mb={2}>
          <Grid item xs={3} container spacing={2}>
            <Grid item xs={12}>
              <AnalyticsChartStatCard
                hideTrendedSpark
                noToolbar
                forceComparison
                chart={{
                  name: "",
                  dimension: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                  template: ApiChartTemplate.SNAPSHOT,
                  type: ApiChartType.GRAPH,
                  attributes,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_LEADS_COUNT],
                    },
                  ],
                  displayOptions: {
                    statTitle: "Leads in this Audience",
                  },
                }}
              />  
            </Grid>
            <Grid item xs={12} sx={{ height: 395 }}>
              <StandardAnalyticsCard
                chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_TOP_CHANNELS_RADAR}
                chart={{
                  name: "Top 5 Marketing Channels Pageviews & Interactions",
                  dimension: [ApiDimension.DIMENSION_CHANNEL],
                  template: ApiChartTemplate.SNAPSHOT,
                  type: ApiChartType.RADAR,
                  attributes,
                  limit: 5,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [
                        ApiMetric.METRIC_PAGEVIEWS_COUNT,
                        ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      ],
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_CHANNEL_INSIGHTS}
              chart={{
                name: "Marketing Channel Insights",
                dimension: [ApiDimension.DIMENSION_CHANNEL],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                attributes,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [
                      ApiMetric.METRIC_SESSIONS_COUNT,
                      ApiMetric.METRIC_PAGEVIEWS_COUNT,
                      ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    ],
                  },
                ],
                displayOptions: {
                  noFilters: true,
                },
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_PRODUCT_CATEGORY_INSIGHTS}
              chart={{
                name: `Products Category Insights`,
                dimension: [ApiDimension.DIMENSION_PRODUCT_CATEGORY],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                attributes,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [
                      ApiMetric.METRIC_PAGEVIEWS_COUNT,
                      ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_PRODUCT_REVENUE,
                    ],
                  },
                ],
                displayOptions: {
                  noFilters: true,
                },
              }}
            />
          </Grid>

          <Grid item xs={3} container spacing={2}>
            <Grid item xs={12}>
              <AnalyticsChartStatCard
                hideTrendedSpark
                noToolbar
                forceComparison
                chart={{
                  name: "",
                  dimension: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                  template: ApiChartTemplate.SNAPSHOT,
                  type: ApiChartType.GRAPH,
                  attributes,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                    },
                  ],
                  displayOptions: {
                    statTitle: "Pageviews by this Audience",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ height: 395 }}>
              <StandardAnalyticsCard
                chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_TOP_PRODUCT_CATEGORY_RADAR}
                chart={{
                  name: "Top 5 Product Category Pageviews & Interactions",
                  dimension: [ApiDimension.DIMENSION_PRODUCT_CATEGORY],
                  template: ApiChartTemplate.SNAPSHOT,
                  type: ApiChartType.RADAR,
                  attributes,
                  limit: 5,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [
                        ApiMetric.METRIC_PAGEVIEWS_COUNT,
                        ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      ],
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={9} container spacing={2}>
            <Grid item xs={12} sx={{ minHeight: 380 }}>
              <StandardAnalyticsCard
                chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_AUDIENCE_PRIMARY_METRICS}
                chart={{
                  name: "Audience Primary Metrics",
                  dimension: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                  template: ApiChartTemplate.TABLE,
                  type: ApiChartType.TABLE,
                  attributes,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [
                        ApiMetric.METRIC_SESSIONS_COUNT,
                        ApiMetric.METRIC_NEW_SESSIONS_COUNT,
                        ApiMetric.METRIC_RETURNING_SESSIONS_COUNT,
                        ApiMetric.METRIC_PAGEVIEWS_COUNT,
                        ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE,
                        ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                        ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
                        ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                      ],
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} sx={{ minHeight: 380 }}>
              <StandardAnalyticsCard
                chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_SESSIONS_AND_PAGEVIEWS}
                chart={{
                  name: "Audience Sessions & Pageviews",
                  dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
                  template: ApiChartTemplate.TREND,
                  type: ApiChartType.GRAPH,
                  attributes,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                    },
                    {
                      key: "react",
                      fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <StandardAnalyticsCard
                chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_AVG_TIME_ON_PAGE}
                chart={{
                  name: "Audience Avg. Time Spent on Page",
                  dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
                  template: ApiChartTemplate.TREND,
                  type: ApiChartType.GRAPH,
                  attributes,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE],
                      chart: {
                        [ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE]:
                          ApiYAxisChartType.AREA,
                      },
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} sx={{ minHeight: 380 }}>
              <StandardAnalyticsCard
                chartKey={
                  CHART_KEY_AUDIENCE_DEEP_DIVE_EFFECTIVE_SESSIONS_AND_PAGEVIEWS
                }
                chart={{
                  name: "Audience Effective Sessions & Pageviews",
                  dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
                  template: ApiChartTemplate.TREND,
                  type: ApiChartType.GRAPH,
                  attributes,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT],
                    },
                    {
                      key: "r",
                      fields: [ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT],
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <StandardAnalyticsCard
                chartKey={
                  CHART_KEY_AUDIENCE_DEEP_DIVE_INTERACTIONS_AND_CONVERSIONS
                }
                chart={{
                  name: "Audience Interactions & Conversions",
                  dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
                  template: ApiChartTemplate.TREND,
                  type: ApiChartType.GRAPH,
                  attributes,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT],
                      chart: {
                        [ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT]:
                          ApiYAxisChartType.AREA,
                      },
                    },
                    {
                      key: "r",
                      fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                      chart: {
                        [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT]:
                          ApiYAxisChartType.AREA,
                      },
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_PRODUCT_PRIMARY_METRICS}
              chart={{
                name: "Product Primary Metrics",
                dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                attributes,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [
                      ApiMetric.METRIC_PAGEVIEWS_COUNT,
                      ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_PRODUCT_REVENUE,
                    ],
                  },
                ],
                displayOptions: {
                  noFilters: true,
                  rowsPerPage: 15,
                  filterDropdowns: [ApiDimension.DIMENSION_PRODUCT_CATEGORY],
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sx={{ minHeight: 380 }}>
            <StandardAnalyticsCard
              chartKey={
                CHART_KEY_AUDIENCE_DEEP_DIVE_LANDING_PAGE_PRIMARY_METRICS
              }
              chart={{
                name: "Landing Page Primary Metrics",
                dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                attributes,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [
                      ApiMetric.METRIC_SESSIONS_COUNT,
                      ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                      ApiMetric.METRIC_PAGEVIEWS_COUNT,
                      ApiMetric.METRIC_AVERAGE_EFFECTIVE_PAGEVIEWS_RATE_DURING_SESSION,
                      ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_LEADS_COUNT,
                      ApiMetric.METRIC_NEW_LEADS_COUNT,
                      ApiMetric.METRIC_RETURNING_LEADS_COUNT,
                      ApiMetric.METRIC_DESKTOP_SESSIONS_COUNT,
                      ApiMetric.METRIC_MOBILE_SESSIONS_COUNT,
                    ],
                  },
                ],
                displayOptions: {
                  noFilters: true,
                },
              }}
            />
          </Grid>

          <Grid item xs={9} sx={{ minHeight: 450 }}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_CHANNEL_PRIMARY_METRICS}
              chart={{
                name: "Marketing Channel Primary Metrics",
                dimension: [ApiDimension.DIMENSION_CHANNEL],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                attributes,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [
                      ApiMetric.METRIC_SESSIONS_COUNT,
                      ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                      ApiMetric.METRIC_PAGEVIEWS_COUNT,
                      ApiMetric.METRIC_AVERAGE_EFFECTIVE_PAGEVIEWS_RATE_DURING_SESSION,
                      ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_LEADS_COUNT,
                      ApiMetric.METRIC_NEW_LEADS_COUNT,
                      ApiMetric.METRIC_RETURNING_LEADS_COUNT,
                      ApiMetric.METRIC_DESKTOP_SESSIONS_COUNT,
                      ApiMetric.METRIC_MOBILE_SESSIONS_COUNT,
                    ],
                  },
                ],
                displayOptions: {
                  noFilters: true,
                },
              }}
            />
          </Grid>

          <Grid item xs={3}>
            <StandardAnalyticsCard
              chartKey={
                CHART_KEY_AUDIENCE_DEEP_DIVE_VISITS_PRIOR_CONVERSION_BY_CHANNEL
              }
              chart={{
                name: "No. of Visits Prior Conversion by Channel",
                dimension: [
                  ApiDimension.DIMENSION_VISITS_SINCE_PRIOR_MACRO_CONVERSION,
                ],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                orderBy: `${ApiDimension.DIMENSION_VISITS_SINCE_PRIOR_MACRO_CONVERSION}`,
                attributes,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_LEADS_COUNT],
                    chart: {
                      [ApiMetric.METRIC_LEADS_COUNT]: ApiYAxisChartType.BAR,
                    },
                  },
                ],
                displayOptions: {
                  noFilters: true,
                  filterDropdowns: [ApiDimension.DIMENSION_CHANNEL],
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sx={{ minHeight: 450 }}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_AUDIENCE_DEEP_DIVE_CHANNEL_ROI_LTV_METRICS}
              chart={{
                name: "Marketing Channel ROI & LTV Metrics",
                dimension: [ApiDimension.DIMENSION_CHANNEL],
                template: ApiChartTemplate.TABLE,
                type: ApiChartType.TABLE,
                attributes,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [
                      ApiMetric.METRIC_ROI,
                      ApiMetric.METRIC_LTV,
                      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                      ApiMetric.METRIC_REVENUE,
                      ApiMetric.METRIC_CPA,
                      ApiMetric.METRIC_CAC,
                    ],
                  },
                ],
                displayOptions: {
                  noFilters: true,
                },
              }}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
}

function AudienceDeepDivePage() {
  return (
    <AudienceAnalyticsProvider>
      <AudienceDeepDivePageInner />
    </AudienceAnalyticsProvider>
  );
}

export default AudienceDeepDivePage;
